/** Create / edit dialog for a notification block (react-hook-form + zod).
 *
 *  A block is a named, typed value referenced in templates as
 *  `block.<codename>`. `tenant` is clearable: cleared = a system-wide block
 *  (`tour_operator: null`). The `definition` field's label + helper text adapt
 *  to the selected `type`, and two type-specific controls appear on demand:
 *  `is_json` (constant) and `template_language` (template). The `template`
 *  type edits its definition per target format (HTML (default) / Markdown /
 *  Plaintext tabs; empty format definitions fall back to the base) plus
 *  per-channel overrides (delivery resolves channel override → format
 *  definition → HTML (default)).
 *
 *  Two types replace the definition textarea with a structured editor:
 *  `rule` embeds the shared `RulesEditor` and stores its rows as inline-rules
 *  JSON (no standalone ruleset needed), and `tag` ("ephemeral tag") picks the
 *  tags to inject with the shared `TagConditionInput` plus an optional
 *  condition (inline rules or a ruleset spec). Notification_Block has no
 *  optimistic-concurrency token, so writes are last-writer-wins. */
import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import { getRegistry as getFeatureRegistry } from '@/api/notificationFeatures';
import { listBlockFunctions, listRulesets, type RulesetRef } from '@/api/notificationLookups';
import { getMergedEventSchema } from '@/api/notifications';
import { notificationRulesetTagLookups } from '@/api/tagLookups';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import {
  TagConditionInput,
  EMPTY_TAG_CONDITION,
  fromWireChips,
  toWireChipsNameOnly,
  type TagConditionFetchers,
  type TagConditionValue,
  type WireTagChipRead,
} from '@/components/TagConditionInput';
import { TemplateCodeEditor } from '@/components/TemplateCodeEditor';
import type { ContextSchemaSource } from '@/components/TemplateCodeEditor/types';
import { BlockTestPanel } from './BlockTestPanel';
import {
  draftsFromInline,
  inlineFromDrafts,
  parseRuleDefinition,
  parseTagDefinition,
  ruleDefinitionFrom,
  tagDefinitionFrom,
} from './blockInlineRules';
import { RulesEditor } from './rules/RulesEditor';
import type { RuleDraft } from './rules/ruleTypes';
import { MATCH_ALL } from '@/api/notificationRulesets';
import {
  create as createBlock,
  update as updateBlock,
  BLOCK_FORMATS,
  BLOCK_TYPE_VALUES,
  ERROR_MODES,
  OVERRIDE_CHANNELS,
  TEMPLATE_LANGUAGES,
  BLOCK_TYPES,
  type BlockFormat,
  type NotificationBlockDetail,
  type NotificationBlockType,
  type NotificationBlockWrite,
  type OverrideChannel,
} from '@/api/notificationBlocks';
import { AppDialog } from '@/components/AppDialog';
import { useEditorHistoryTabs } from '@/components/History/useEditorHistoryTabs';
import { FieldHelp, labelWithHelp } from '@/components/FieldHelp';
import { HelpLink } from '@/components/HelpLink';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';
import { singlePageTenant } from '@/scope/pageTenants';

import { BLOCK_HELP } from './fieldHelp';

import { CloneBlockDialog } from './CloneBlockDialog';

/** Per-type `definition` field presentation. */
const DEFINITION_FIELD: Record<
  NotificationBlockType,
  { label: string; helper: string; minRows: number }
> = {
  template: {
    label: 'Template source',
    helper: 'The template body rendered for this block.',
    minRows: 4,
  },
  ruleset: {
    label: 'Ruleset spec (id / codename / boolean expr)',
    helper: 'A ruleset id, a ruleset codename, or a boolean expression.',
    minRows: 3,
  },
  dotty: {
    label: 'Dotty path',
    helper: 'Dot-separated lookup path, e.g. booking.guest.first_name.',
    minRows: 2,
  },
  jsonpath: {
    label: 'JSONPath expression',
    helper: 'A JSONPath expression, e.g. $.booking.guest.first_name.',
    minRows: 2,
  },
  function: {
    label: 'Function name',
    helper: 'Name of a registered resolver function.',
    minRows: 2,
  },
  constant: {
    label: 'Constant value',
    helper: 'A literal value. Enable "Parse as JSON" to decode it as JSON.',
    minRows: 3,
  },
  rule: {
    label: 'Rules',
    helper: 'Evaluates to true or false — no standalone ruleset needed.',
    minRows: 3,
  },
  tag: {
    label: 'Tags to inject',
    helper: 'Adds the referenced tags to the rendering context under this block.',
    minRows: 3,
  },
};

/** Tag lookups for the `tag` type's chip picker. Mounted on the rulesets
 *  viewset, whose lookup actions accept `console_notification_blocks_view`
 *  (one binding serves the block, ruleset and notification editors). */
const TAG_FETCHERS: TagConditionFetchers = {
  fetchTenantCategories: (tenantId, q, signal) =>
    notificationRulesetTagLookups.tenantCategories(tenantId, q, signal),
  fetchAllCategoryCodes: (q, signal) => notificationRulesetTagLookups.categoryCodes(q, signal),
  fetchCategoryTags: (categoryId, q, signal) =>
    notificationRulesetTagLookups.categoryTags(categoryId, q, signal),
  fetchTagsForCode: (categoryCode, q, signal) =>
    notificationRulesetTagLookups.tagsForCode(categoryCode, q, signal),
};

/** A `tag` block's condition kind, as the dialog models it. */
const TAG_CONDITION_KINDS = [
  { value: 'none', label: 'Always inject' },
  { value: 'rules', label: 'Only when rules match' },
  { value: 'ruleset', label: 'Only when a ruleset matches' },
] as const;
type TagConditionKind = (typeof TAG_CONDITION_KINDS)[number]['value'];

const FormSchema = z.object({
  codename: z
    .string()
    .trim()
    .min(1, 'Codename is required')
    .max(128)
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      'Letters, digits and underscore only; cannot start with a digit.',
    ),
  name: z.string().max(256),
  description: z.string(),
  // Derived from BLOCK_TYPES so a new block type can never be silently
  // rejected here after being added to the catalog.
  type: z.enum(BLOCK_TYPE_VALUES),
  definition: z.string(),
  definition_markdown: z.string(),
  definition_plaintext: z.string(),
  template_language: z.enum(['django', 'jinja2']),
  is_json: z.boolean(),
  default_value: z.string(),
  error_mode: z.enum(['soft', 'hard']),
  enabled: z.boolean(),
  feature: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  codename: '',
  name: '',
  description: '',
  type: 'template',
  definition: '',
  definition_markdown: '',
  definition_plaintext: '',
  template_language: 'django',
  is_json: false,
  default_value: '',
  error_mode: 'soft',
  enabled: true,
  feature: '',
};

function toDefaults(b: NotificationBlockDetail | null): FormValues {
  if (!b) return EMPTY;
  return {
    codename: b.codename,
    name: b.name ?? '',
    description: b.description ?? '',
    type: b.type,
    definition: b.definition ?? '',
    definition_markdown: b.definition_markdown ?? '',
    definition_plaintext: b.definition_plaintext ?? '',
    template_language: b.template_language ?? 'django',
    is_json: b.is_json ?? false,
    default_value: b.default_value ?? '',
    error_mode: b.error_mode ?? 'soft',
    enabled: b.enabled,
    feature: b.feature ?? '',
  };
}

/** One channel-override editor row (local state, outside RHF). `key` is a
 *  stable React key for remove. */
interface OverrideRow {
  key: string;
  channel: OverrideChannel;
  definition: string;
}

let overrideKeySeq = 0;
const nextOverrideKey = () => `override-${overrideKeySeq++}`;

function toOverrideRows(b: NotificationBlockDetail | null): OverrideRow[] {
  const overrides = b?.channel_overrides ?? {};
  // Stable display order: the OVERRIDE_CHANNELS order, not object-key order.
  return OVERRIDE_CHANNELS.filter((c) => overrides[c.value] !== undefined).map((c) => ({
    key: nextOverrideKey(),
    channel: c.value,
    definition: overrides[c.value] ?? '',
  }));
}

/** Per-format `definition` tab presentation (`template` type only). */
const FORMAT_TAB: Record<
  BlockFormat,
  { field: 'definition' | 'definition_markdown' | 'definition_plaintext'; helper: string }
> = {
  html: {
    field: 'definition',
    helper: 'The base template body — used when no format definition or channel override applies.',
  },
  markdown: {
    field: 'definition_markdown',
    helper:
      'Rendered when the delivery target format is markdown. Empty — falls back to HTML (default).',
  },
  plaintext: {
    field: 'definition_plaintext',
    helper:
      'Rendered when the delivery target format is plaintext. Empty — falls back to HTML (default).',
  },
};

interface Props {
  open: boolean;
  /** null = create; a record = edit. */
  block: NotificationBlockDetail | null;
  /** The page's operators (D9), for create — and for "Clone to tenant…" of a
   *  system block: the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
}

export function NotificationBlockFormDialog({ open, block, pageTenants, onClose }: Props) {
  const [cloning, setCloning] = useState(false);
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const isEdit = block !== null;
  const canManage = hasPerm('console_notification_blocks_manage');
  const history = useEditorHistoryTabs({
    enabled: isEdit && hasPerm('console_audit_view'),
    contentType: 'demo.notification_block',
    objectPk: block?.id ?? null,
    onRestored: () => void qc.invalidateQueries({ queryKey: ['notification_blocks'] }),
  });

  // Tenant + channel-override rows live outside RHF (self-contained controlled
  // inputs, like the launch policy editor). null tenant = system-wide.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  // Which per-format definition tab is shown (template type only).
  const [defTab, setDefTab] = useState<BlockFormat>('html');
  // Structured editors for the `rule` / `tag` types. Like `overrides` these
  // live outside RHF: they are self-contained controlled editors whose value is
  // serialized into `definition` / `tags` at submit time.
  const [ruleDrafts, setRuleDrafts] = useState<RuleDraft[]>([]);
  const [ruleMatchType, setRuleMatchType] = useState<number>(MATCH_ALL);
  const [tagChips, setTagChips] = useState<TagConditionValue>(EMPTY_TAG_CONDITION);
  const [tagCondKind, setTagCondKind] = useState<TagConditionKind>('none');
  const [tagRulesetSpec, setTagRulesetSpec] = useState('');
  // Field-keyed 400s (the rule/tag types have real server-side validation:
  // JSON shape, unknown templates, foreign-tenant tags).
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toDefaults(block),
  });

  useEffect(() => {
    if (!open) return;
    history.reset();
    reset(toDefaults(block));
    setOverrides(toOverrideRows(block));
    setDefTab('html');
    setServerErrors({});

    // Hydrate the structured editors from the stored definition / chips. A
    // definition that does not parse (hand-edited, or a type switch) yields an
    // empty editor rather than an error — the raw text is still in `definition`
    // until the user saves.
    const inline = block?.type === 'rule' ? parseRuleDefinition(block.definition) : null;
    const hydrated = draftsFromInline(inline);
    setRuleDrafts(hydrated.drafts);
    setRuleMatchType(hydrated.matchType);

    const condition = block?.type === 'tag' ? parseTagDefinition(block.definition) : null;
    if (condition?.kind === 'ruleset') {
      setTagCondKind('ruleset');
      setTagRulesetSpec(condition.spec);
      setRuleDrafts([]);
      setRuleMatchType(MATCH_ALL);
    } else if (condition?.kind === 'rules') {
      const cond = draftsFromInline(condition);
      setTagCondKind('rules');
      setTagRulesetSpec('');
      setRuleDrafts(cond.drafts);
      setRuleMatchType(cond.matchType);
    } else {
      setTagCondKind('none');
      setTagRulesetSpec('');
    }
    setTagChips(
      block?.type === 'tag'
        ? fromWireChips((block.tags ?? []) as WireTagChipRead[], 'and')
        : EMPTY_TAG_CONDITION,
    );

    if (block) {
      setTenant(
        block.tenant
          ? { id: block.tenant.id, name: block.tenant.name, code: block.tenant.code }
          : null,
      );
    } else {
      setTenant(singlePageTenant(pageTenants));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, block?.id]);

  const type = watch('type');
  const def = DEFINITION_FIELD[type];
  const definition = watch('definition');

  const tenantId = tenant?.id ?? null;

  // Registered predefined functions — gate the "Function" type + populate its picker.
  const { data: functionNames } = useQuery({
    queryKey: ['notification-block-functions'],
    queryFn: listBlockFunctions,
    staleTime: 60_000,
  });
  const functions = functionNames ?? [];
  const functionsEmpty = functionNames !== undefined && functions.length === 0;

  // Feature registry for the gating dropdown. May 403 for users without the
  // notification-features view perm — then only "None" (and the block's
  // current value, if any) is offered.
  const { data: featureRegistry } = useQuery({
    queryKey: ['notification-features', 'registry'],
    queryFn: getFeatureRegistry,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const featureDefs = featureRegistry?.features ?? [];

  // The ruleset autocomplete displays a value synthesized from the current
  // `definition` (a codename), so editing hydrates without an extra fetch.
  const refValue = useMemo(
    () => (definition ? { id: -1, codename: definition, name: '', tenant: null } : null),
    [definition],
  );
  const setDefinition = (v: string) => setValue('definition', v, { shouldDirty: true });

  // ---- channel-override rows (each channel at most once — the row selects
  // disable channels already taken, and "Add" picks the first free one) ----
  const usedChannels = new Set(overrides.map((r) => r.channel));
  const freeChannel = OVERRIDE_CHANNELS.find((c) => !usedChannels.has(c.value));
  const addOverride = () => {
    if (!freeChannel) return;
    setOverrides((rs) => [
      ...rs,
      { key: nextOverrideKey(), channel: freeChannel.value, definition: '' },
    ]);
  };
  const patchOverride = (key: string, patch: Partial<OverrideRow>) =>
    setOverrides((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeOverride = (key: string) => setOverrides((rs) => rs.filter((r) => r.key !== key));
  const overridesPayload = (): Partial<Record<OverrideChannel, string>> =>
    Object.fromEntries(overrides.map((r) => [r.channel, r.definition]));

  // ---- `rule` / `tag` serialization ----------------------------------------
  // Blocks are event-agnostic, so expression-rule and template completions
  // come from the union of every event's context schema rather than one
  // event's.
  const { data: mergedSchema } = useQuery({
    queryKey: ['notification-events', 'merged-schema'],
    queryFn: () => getMergedEventSchema('all'),
    staleTime: 5 * 60_000,
    retry: false,
    enabled:
      open &&
      (type === 'template' || type === 'rule' || (type === 'tag' && tagCondKind === 'rules')),
  });
  const contextSchemas: ContextSchemaSource[] | undefined = useMemo(
    () =>
      mergedSchema
        ? [
            {
              id: 'merged',
              label: 'Any event (merged)',
              schema: mergedSchema as unknown as Record<string, unknown>,
            },
          ]
        : undefined,
    [mergedSchema],
  );

  /** The `definition` string for the current type — structured editors win. */
  const definitionFor = (v: FormValues): string => {
    if (v.type === 'rule') return ruleDefinitionFrom(inlineFromDrafts(ruleMatchType, ruleDrafts));
    if (v.type === 'tag') {
      if (tagCondKind === 'ruleset') {
        return tagDefinitionFrom({ kind: 'ruleset', spec: tagRulesetSpec });
      }
      if (tagCondKind === 'rules') {
        const inline = inlineFromDrafts(ruleMatchType, ruleDrafts);
        return tagDefinitionFrom({ kind: 'rules', ...inline });
      }
      return '';
    }
    return v.definition;
  };

  /** Tag chips always ride along: `[]` on a non-tag type clears any references
   *  left behind by a type switch, so no inert chip row keeps pinning a tag. */
  // Name-only: a tag block's reference injects ONE tag's data, so a canonical
  // chip — which matches many by design — is not expressible here. The builder
  // never offers the path (no fetchCanonicalsForCode above); this keeps the
  // narrower write type honest rather than relying on that.
  const tagsPayload = (v: FormValues) => (v.type === 'tag' ? toWireChipsNameOnly(tagChips) : []);

  // Snapshot of the current form values as an (unsaved) block payload — used by the
  // test panel to evaluate exactly what the user is editing.
  const buildBlockPayload = (): NotificationBlockWrite => {
    const v = getValues();
    return {
      tour_operator: tenantId,
      codename: v.codename.trim() || 'test',
      name: v.name,
      description: v.description,
      type: v.type,
      definition: definitionFor(v),
      definition_markdown: v.definition_markdown,
      definition_plaintext: v.definition_plaintext,
      channel_overrides: overridesPayload(),
      template_language: v.template_language,
      is_json: v.is_json,
      default_value: v.default_value === '' ? null : v.default_value,
      error_mode: v.error_mode,
      enabled: v.enabled,
      feature: v.feature === '' ? null : v.feature,
      tags: tagsPayload(v),
    };
  };

  const mutation = useMutation({
    mutationFn: (body: NotificationBlockWrite) =>
      isEdit ? updateBlock(block.id, body) : createBlock(body),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Block updated.' : 'Block created.', 'success');
      qc.invalidateQueries({ queryKey: ['notification_blocks'] });
      onClose();
    },
    onError: (e) => {
      // The rule/tag types validate server-side (JSON shape, unknown rule
      // templates, foreign-tenant tags) — surface those against their field
      // instead of only in a snackbar.
      if (e instanceof ConsoleApiError && e.status === 400 && e.body && typeof e.body === 'object') {
        const out: Record<string, string> = {};
        for (const [key, msg] of Object.entries(e.body as Record<string, unknown>)) {
          out[key] = Array.isArray(msg) ? msg.join(' ') : String(msg);
        }
        setServerErrors(out);
        snackbar.show('Save failed — see the highlighted field.', 'error');
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
    },
  });

  const submit = handleSubmit((v) => {
    setServerErrors({});
    mutation.mutate({
      tour_operator: tenantId,
      codename: v.codename.trim(),
      name: v.name.trim(),
      description: v.description.trim(),
      type: v.type,
      definition: definitionFor(v),
      definition_markdown: v.definition_markdown,
      definition_plaintext: v.definition_plaintext,
      channel_overrides: overridesPayload(),
      template_language: v.template_language,
      is_json: v.is_json,
      default_value: v.default_value === '' ? null : v.default_value,
      error_mode: v.error_mode,
      enabled: v.enabled,
      feature: v.feature === '' ? null : v.feature,
      tags: tagsPayload(v),
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      title={
        <>
          {isEdit ? `Edit block · ${block.codename}` : 'New block'}{' '}
          <HelpLink story="notification-blocks" newTab />
        </>
      }
      disableBackdropClose
      actions={
        history.showingHistory ? (
          <Button onClick={onClose}>Close</Button>
        ) : (
          <>
            {/* Offered only on a saved SYSTEM block, because that is the only
                case where editing in place would change what every other tenant
                renders. Cloning is the way out that needs no library permission. */}
            {isEdit && !block.tenant && (
              <Button onClick={() => setCloning(true)} sx={{ mr: 'auto' }}>
                Clone to tenant…
              </Button>
            )}
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={mutation.isPending} onClick={submit}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create block'}
            </Button>
          </>
        )
      }
    >
      {isEdit && cloning && (
        <CloneBlockDialog
          open
          block={block}
          onClose={() => setCloning(false)}
          onCloned={onClose}
          pageTenants={pageTenants}
        />
      )}
      {isEdit && block.pack?.status === 'managed' && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          This block is maintained by the notification pack ({block.pack.version ?? 'unversioned'}).
          Saving an edit here opts it out of future pack updates — clone it to a tenant instead if
          you only need it different for one operator.
        </Alert>
      )}
      {history.header}
      {history.historyPane ?? (
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            {...register('codename')}
            label="Codename"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.codename)}
            helperText={errors.codename?.message ?? 'Referenced in templates as block.<codename>.'}
            InputProps={{ endAdornment: <FieldHelp help={BLOCK_HELP.codename} /> }}
          />
          <TextField
            {...register('name')}
            label="Name"
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            error={Boolean(errors.name)}
            helperText={errors.name?.message ?? 'Optional human-readable label.'}
            InputProps={{ endAdornment: <FieldHelp help={BLOCK_HELP.name} /> }}
          />
        </Box>

        <TextField
          {...register('description')}
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          error={Boolean(errors.description)}
          helperText={errors.description?.message ?? 'Optional.'}
          InputProps={{ endAdornment: <FieldHelp help={BLOCK_HELP.description} /> }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <TenantPicker
              value={tenant}
              featured={isEdit ? undefined : pageTenants}
              onChange={setTenant}
              label="Operator"
              placeholder="System / global (no operator)"
            />
          </Box>
          <FieldHelp help={BLOCK_HELP.operator} />
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <TextField
                select
                label="Type"
                size="small"
                sx={{ width: 240 }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as NotificationBlockType)}
              >
                {BLOCK_TYPES.map((t) => {
                  const disabled = t.value === 'function' && functionsEmpty;
                  return (
                    <MenuItem key={t.value} value={t.value} disabled={disabled}>
                      {t.label}
                      {disabled ? ' (none registered)' : ''}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
          />
          <FieldHelp help={BLOCK_HELP.type} />
          <Controller
            control={control}
            name="error_mode"
            render={({ field }) => (
              <TextField
                select
                label="Error mode"
                size="small"
                sx={{ width: 280 }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
              >
                {ERROR_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <FieldHelp help={BLOCK_HELP.errorMode} />
          <Controller
            control={control}
            name="feature"
            render={({ field }) => (
              <TextField
                select
                label="Feature"
                size="small"
                sx={{ width: 280 }}
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                helperText="Gated blocks render only while the feature is enabled at every applicable level."
              >
                <MenuItem value="">None (always available)</MenuItem>
                {featureDefs.map((f) => (
                  <MenuItem key={f.codename} value={f.codename}>
                    {f.name}
                  </MenuItem>
                ))}
                {field.value !== '' && !featureDefs.some((f) => f.codename === field.value) && (
                  <MenuItem value={field.value}>{field.value}</MenuItem>
                )}
              </TextField>
            )}
          />
          <FieldHelp help={BLOCK_HELP.feature} />
        </Box>

        {type === 'ruleset' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ flexGrow: 1 }}>
              <RemoteAutocomplete<RulesetRef>
                label={def.label}
                size="small"
                fetchOnEmpty
                value={refValue}
                getOptionLabel={(o) =>
                  o.name ? `${o.codename ?? o.id} — ${o.name}` : String(o.codename ?? o.id)
                }
                getOptionKey={(o) => o.codename ?? `id:${o.id}`}
                queryFn={async (q) => {
                  const r = await listRulesets({
                    search: q,
                    pageSize: 20,
                    ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
                  });
                  return r.results;
                }}
                onChange={(v) => {
                  const opt = v as RulesetRef | null;
                  setDefinition(opt ? (opt.codename ?? String(opt.id)) : '');
                }}
              />
            </Box>
            <FieldHelp help={BLOCK_HELP.definition} />
          </Box>
        ) : type === 'function' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Controller
              control={control}
              name="definition"
              render={({ field }) => {
                const opts =
                  field.value && !functions.includes(field.value)
                    ? [field.value, ...functions]
                    : functions;
                return (
                  <TextField
                    select
                    label={def.label}
                    size="small"
                    fullWidth
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={Boolean(errors.definition)}
                    helperText={
                      errors.definition?.message ??
                      (functionsEmpty ? 'No predefined functions are registered.' : def.helper)
                    }
                  >
                    {opts.map((fn) => (
                      <MenuItem key={fn} value={fn}>
                        {fn}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
            <FieldHelp help={BLOCK_HELP.definition} />
          </Box>
        ) : type === 'template' ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tabs
                value={defTab}
                onChange={(_, v: BlockFormat) => setDefTab(v)}
                sx={{ mb: 1, minHeight: 36 }}
              >
                {BLOCK_FORMATS.map((f) => (
                  <Tab key={f.value} value={f.value} label={f.label} sx={{ minHeight: 36 }} />
                ))}
              </Tabs>
              <FieldHelp help={BLOCK_HELP.definitionTemplate} />
            </Box>
            <Controller
              key={defTab}
              control={control}
              name={FORMAT_TAB[defTab].field}
              render={({ field }) => (
                <Box>
                  <TemplateCodeEditor
                    value={field.value}
                    onChange={field.onChange}
                    baseLanguage={defTab === 'html' ? 'html' : 'text'}
                    contextSchemas={contextSchemas ?? []}
                    autoGrow
                    maxHeight={480}
                    persistenceKey={`nf-block-${block?.id ?? 'new'}-${FORMAT_TAB[defTab].field}`}
                    placeholder={def.label}
                    aria-label={`${def.label} (${defTab})`}
                  />
                  <FormHelperText
                    error={defTab === 'html' && Boolean(errors.definition)}
                    sx={{ mx: 1.75 }}
                  >
                    {(defTab === 'html' ? errors.definition?.message : undefined) ??
                      FORMAT_TAB[defTab].helper}
                  </FormHelperText>
                </Box>
              )}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
                Channel overrides <FieldHelp help={BLOCK_HELP.channelOverrides} />
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                Delivery picks a channel override first, then the target format&apos;s definition,
                then HTML (default).
              </Typography>
              <Stack spacing={1.5}>
                {overrides.map((row) => (
                  <Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      select
                      label="Channel"
                      size="small"
                      sx={{ width: 150, flexShrink: 0 }}
                      value={row.channel}
                      onChange={(e) =>
                        patchOverride(row.key, { channel: e.target.value as OverrideChannel })
                      }
                    >
                      {OVERRIDE_CHANNELS.map((c) => (
                        <MenuItem
                          key={c.value}
                          value={c.value}
                          disabled={c.value !== row.channel && usedChannels.has(c.value)}
                        >
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Keyed by channel so the base highlighting follows the
                          selected channel (email → HTML); pending edits flush
                          on the remount. */}
                      <TemplateCodeEditor
                        key={row.channel}
                        value={row.definition}
                        onChange={(v) => patchOverride(row.key, { definition: v })}
                        baseLanguage={row.channel === 'email' ? 'html' : 'text'}
                        contextSchemas={contextSchemas ?? []}
                        autoGrow
                        maxHeight={320}
                        placeholder="Definition"
                        aria-label={`Channel override definition (${row.channel})`}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="Remove override"
                      onClick={() => removeOverride(row.key)}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {overrides.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No channel overrides — every channel uses the format definitions above.
                  </Typography>
                )}
              </Stack>
              <Button
                startIcon={<AddIcon />}
                size="small"
                sx={{ mt: 1.5 }}
                onClick={addOverride}
                disabled={!freeChannel}
              >
                Add override
              </Button>
            </Box>
          </Box>
        ) : type === 'rule' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              {def.label} <FieldHelp help={BLOCK_HELP.definitionRule} />
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {def.helper} Read the verdict in templates as{' '}
              <code>{'{% if block.<codename> %}'}</code>.
            </Typography>
            <RulesEditor
              value={ruleDrafts}
              onChange={setRuleDrafts}
              matchType={ruleMatchType}
              onMatchTypeChange={setRuleMatchType}
              tenantId={tenantId ?? 'system'}
              disabled={!canManage}
              emptyMatchesLabel="nothing (add at least one rule)"
              contextSchemas={contextSchemas}
              {...(serverErrors.definition ? { errors: { '*': serverErrors.definition } } : {})}
            />
          </Box>
        ) : type === 'tag' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              {def.label} <FieldHelp help={BLOCK_HELP.definitionTag} />
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {def.helper} Read them in templates with{' '}
              <code>{'{% pick block.<codename> "<category>" as v %}'}</code>.
            </Typography>
            <TagConditionInput
              value={tagChips}
              onChange={setTagChips}
              fetchers={TAG_FETCHERS}
              label="Tags"
              disabled={!canManage}
              hideMatchMode
              helperText={
                serverErrors.tags ??
                'Every referenced tag’s data is merged into this block, keyed by its category.'
              }
              {...(tenantId != null ? { fixedTenant: { id: tenantId } } : {})}
            />
            <Box sx={{ mt: 2 }}>
              <TextField
                select
                label="Condition"
                size="small"
                fullWidth
                value={tagCondKind}
                disabled={!canManage}
                onChange={(e) => setTagCondKind(e.target.value as TagConditionKind)}
                error={Boolean(serverErrors.definition)}
                helperText={
                  serverErrors.definition ??
                  'When the condition fails the block is empty, so templates fall through to their next source.'
                }
              >
                {TAG_CONDITION_KINDS.map((k) => (
                  <MenuItem key={k.value} value={k.value}>
                    {k.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {tagCondKind === 'rules' && (
              <Box sx={{ mt: 2 }}>
                <RulesEditor
                  value={ruleDrafts}
                  onChange={setRuleDrafts}
                  matchType={ruleMatchType}
                  onMatchTypeChange={setRuleMatchType}
                  tenantId={tenantId ?? 'system'}
                  disabled={!canManage}
                  emptyMatchesLabel="always (add a rule to make it conditional)"
                  contextSchemas={contextSchemas}
                />
              </Box>
            )}
            {tagCondKind === 'ruleset' && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <RemoteAutocomplete<RulesetRef>
                    label="Ruleset (id / codename / boolean expr)"
                    size="small"
                    fetchOnEmpty
                    value={
                      tagRulesetSpec
                        ? { id: -1, codename: tagRulesetSpec, name: '', tenant: null }
                        : null
                    }
                    getOptionLabel={(o) =>
                      o.name ? `${o.codename ?? o.id} — ${o.name}` : String(o.codename ?? o.id)
                    }
                    getOptionKey={(o) => o.codename ?? `id:${o.id}`}
                    queryFn={async (q) => {
                      const r = await listRulesets({
                        search: q,
                        pageSize: 20,
                        ...(tenantId != null ? { tour_operator_id: tenantId } : {}),
                      });
                      return r.results;
                    }}
                    onChange={(v) => {
                      const opt = v as RulesetRef | null;
                      setTagRulesetSpec(opt ? (opt.codename ?? String(opt.id)) : '');
                    }}
                  />
                </Box>
                <FieldHelp help={BLOCK_HELP.definition} />
              </Box>
            )}
          </Box>
        ) : (
          <TextField
            {...register('definition')}
            label={def.label}
            size="small"
            fullWidth
            multiline
            minRows={def.minRows}
            error={Boolean(errors.definition ?? serverErrors.definition)}
            helperText={errors.definition?.message ?? serverErrors.definition ?? def.helper}
            InputProps={{
              endAdornment: (
                <FieldHelp
                  help={
                    type === 'jsonpath'
                      ? BLOCK_HELP.definitionJsonpath
                      : type === 'dotty'
                        ? BLOCK_HELP.definitionDotty
                        : BLOCK_HELP.definition
                  }
                />
              ),
            }}
          />
        )}

        {type === 'template' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Controller
              control={control}
              name="template_language"
              render={({ field }) => (
                <TextField
                  select
                  label="Template language"
                  size="small"
                  sx={{ width: 240 }}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {TEMPLATE_LANGUAGES.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <FieldHelp help={BLOCK_HELP.templateLanguage} />
          </Box>
        )}

        {type === 'constant' && (
          <Controller
            control={control}
            name="is_json"
            render={({ field }) => (
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={labelWithHelp('Parse the value as JSON', BLOCK_HELP.isJson)}
              />
            )}
          />
        )}

        <TextField
          {...register('default_value')}
          label="Default value"
          size="small"
          fullWidth
          error={Boolean(errors.default_value)}
          helperText={
            errors.default_value?.message ?? 'Fallback when evaluation errors or is empty.'
          }
          InputProps={{ endAdornment: <FieldHelp help={BLOCK_HELP.defaultValue} /> }}
        />

        <Divider />

        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={labelWithHelp('Enabled', BLOCK_HELP.enabled)}
            />
          )}
        />

        <Divider />

        <Accordion
          disableGutters
          elevation={0}
          sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant="subtitle2">Test this block</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <BlockTestPanel buildBlock={buildBlockPayload} type={type} />
          </AccordionDetails>
        </Accordion>
      </Box>
      )}
    </AppDialog>
  );
}
